import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, ShieldCheck, AlertTriangle, AlertOctagon, Info, Plus, Trash2, Loader2,
  Stethoscope, Clock, Sparkles, FileText, Send, LayoutGrid, Activity, User,
} from 'lucide-react';
import { api } from '../../lib/api';
import { isFirmLike } from '../../lib/orgTypes';

import { useOrg } from '../../hooks/useOrg';
import CaseDocumentsTab from './CaseDocumentsTab';
import { CollectabilityBadge } from '../referrals/CollectabilityBadge';

// Case detail — the matter and its funding path. The Coverage panel is
// the point: an ordered list of payers with what's left on each, the
// derived "bill this" chip, the rule alerts, and (for PIP in a no-fault
// state) the Emergency Medical Condition record that unlocks the full
// benefit. Everything saves inline; the engine recomputes on every save.

const KIND_LABEL = { pip: 'PIP', medpay: 'MedPay', health: 'Health insurance', lop: 'LOP', um_uim: 'UM/UIM', self_pay: 'Self-pay' };
const KIND_CLS = {
  pip: 'bg-warning-soft text-warning', medpay: 'bg-warning-soft text-warning',
  lop: 'bg-accent-glow text-accent-light', health: 'bg-success-soft text-success',
  um_uim: 'bg-tertiary text-muted-c', self_pay: 'bg-tertiary text-muted-c',
};
const STATUSES = ['intake', 'treatment', 'demand', 'negotiation', 'litigation', 'settled', 'closed'];
const STATUS_LABEL = { intake: 'Intake', treatment: 'In treatment', demand: 'Demand', negotiation: 'Negotiation', litigation: 'In litigation', settled: 'Settled', closed: 'Closed' };
const COV_STATUSES = ['active', 'exhausted', 'denied', 'pending', 'signed', 'inactive'];
const STATUS_CLS = {
  intake: 'bg-accent-glow text-accent-light', treatment: 'bg-accent-glow text-accent-light',
  demand: 'bg-warning-soft text-warning', negotiation: 'bg-warning-soft text-warning',
  litigation: 'bg-danger-soft text-danger', settled: 'bg-success-soft text-success', closed: 'bg-tertiary text-muted-c',
};
const TABS = [
  ['overview', 'Overview', LayoutGrid], ['treatment', 'Treatment', Activity], ['documents', 'Documents', FileText],
  ['referrals', 'Referrals', Send], ['timeline', 'Timeline', Clock],
];
const REF_STATUS = { received: 'Awaiting acceptance', accepted: 'Accepted — patient choosing a time', scheduled: 'First visit scheduled', completed: 'Care completed', delivered: 'Records delivered', declined: 'Declined', expired: 'Expired — no answer' };
const TYPE_LABEL = { auto_mva: 'Auto (MVA)', premises: 'Premises', other: 'Other PI' };
const money = (n) => (n == null || n === '' ? '—' : `$${Number(n).toLocaleString()}`);
const fmtD = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const inputClass = 'px-3 py-2 rounded-[10px] bg-tertiary border border-theme text-[13px] text-primary-c focus:outline-none focus:border-strong transition-all';

function AlertRow({ a }) {
  const Icon = a.level === 'critical' ? AlertOctagon : a.level === 'warning' ? AlertTriangle : Info;
  const cls = a.level === 'critical' ? 'bg-danger-soft text-danger' : a.level === 'warning' ? 'bg-warning-soft text-warning' : 'bg-accent-glow text-accent-light';
  return (
    <div className={`flex items-start gap-2 px-3 py-2 rounded-[10px] text-[12.5px] font-medium ${cls}`}>
      <Icon size={14} className="mt-0.5 shrink-0" /> <span>{a.text}</span>
    </div>
  );
}

export default function CaseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentOrg } = useOrg();
  const [c, setC] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [add, setAdd] = useState({ kind: 'health', carrier: '', claim_number: '', limit_amount: '' });
  const [emc, setEmc] = useState({ status: 'determined', provider_name: '', determined_at: '' });
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'overview');
  const [refs, setRefs] = useState(null);     // referrals on this case
  const [docCount, setDocCount] = useState(null);

  const load = useCallback(async () => {
    if (!currentOrg?.id) return;
    try { setC(await api.getCase(currentOrg.id, id)); setError(''); }
    catch (err) { setError(err.message); }
  }, [currentOrg?.id, id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!currentOrg?.id || !c?.id) return;
    api.listReferrals(currentOrg.id).then((rows) => setRefs((rows || []).filter((r) => r.case_id === c.id || (!r.case_id && r.client_id === c.client_id)))).catch(() => setRefs([]));
    api.listCaseDocuments(currentOrg.id, c.id).then((d) => setDocCount((d || []).length)).catch(() => setDocCount(0));
  }, [currentOrg?.id, c?.id, c?.client_id]);

  const run = async (fn) => {
    setBusy(true); setError('');
    try { const next = await fn(); if (next) setC((prev) => ({ ...prev, ...next, events: next.events || prev.events })); await load(); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  if (!c && !error) return <p className="text-[13px] text-muted-c flex items-center gap-2 py-12 justify-center"><Loader2 size={15} className="animate-spin" /> Loading case…</p>;
  if (!c) return <div className="max-w-[760px] mx-auto px-6 py-12 text-[13px] text-red-400">{error}</div>;

  const f = c.funding || { path: [], alerts: [], current: {} };
  const pip = (c.coverages || []).find((x) => x.kind === 'pip');
  const clientName = `${c.client?.first_name || ''} ${c.client?.last_name || ''}`.trim();
  const isFirm = isFirmLike(currentOrg?.type);

  return (
    <div className="max-w-[1000px] mx-auto px-4 md:px-7 py-6">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-muted-c hover:text-accent-light mb-4">
        <ArrowLeft size={13} /> Back
      </button>

      {error && <div className="text-[12px] text-red-400 bg-red-500/10 border border-red-500/20 rounded-[10px] px-3.5 py-2.5 mb-4">{error}</div>}

      {/* Header band */}
      <div className="bg-elevated border border-theme rounded-[16px] overflow-hidden mb-4">
        <div className="h-1 gradient-accent" />
        <div className="px-5 md:px-6 pt-5 pb-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-[52px] h-[52px] rounded-[14px] bg-accent-glow text-accent-light flex items-center justify-center text-[16px] font-bold shrink-0">
                {(c.client?.first_name?.[0] || '?')}{(c.client?.last_name?.[0] || '')}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] font-mono text-muted-c">{c.case_number}</span>
                  <span className={`px-2 py-0.5 rounded-md text-[10.5px] font-bold ${STATUS_CLS[c.status] || 'bg-tertiary text-muted-c'}`}>{STATUS_LABEL[c.status] || c.status}</span>
                  <span className="px-2 py-0.5 rounded-md text-[10.5px] font-bold bg-tertiary text-secondary-c">{TYPE_LABEL[c.case_type] || c.case_type}</span>
                </div>
                <h1 className="text-[22px] font-bold text-primary-c leading-tight mt-0.5 truncate">
                  <Link to={`/app/contacts/${c.client?.id}`} className="hover:text-accent-light">{clientName || 'Client'}</Link>
                </h1>
                <div className="text-[12.5px] text-muted-c mt-0.5 flex items-center gap-1.5 flex-wrap">
                  <span>Injured {fmtD(c.accident_date)}</span>{c.accident_state && <span>· {c.accident_state}</span>}{c.firm?.name && <span>· {c.firm.name}</span>}
                  {c.client?.phone && <span>· {c.client.phone}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select value={c.status} onChange={(e) => run(() => api.updateCase(currentOrg.id, c.id, { status: e.target.value }))} className={inputClass} title="Case status">
                {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
              <Link to={`/app/contacts/${c.client?.id}`} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[10px] text-[12px] font-semibold border border-theme bg-tertiary text-secondary-c hover:bg-hover-surface"><User size={12} /> Contact</Link>
              {isFirm && (
                <Link to={`/app/referrals/concierge?client=${c.client?.id}&return=${encodeURIComponent(`/app/cases/${c.id}`)}`} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[10px] text-[12px] font-semibold text-white gradient-accent shadow-glow whitespace-nowrap">
                  <Sparkles size={12} /> Refer a case
                </Link>
              )}
            </div>
          </div>

          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5 mt-5">
            {[
              ['Bills to', f.current?.chip || 'No coverage', f.alerts?.[0]?.text || (f.rules?.has_pip ? `${f.rules.state} no-fault rules` : null), f.alerts?.[0]?.level],
              ['Visits', c.treatment ? `${c.treatment.visits_completed} completed` : '—', c.treatment?.alerts?.[0]?.text || (c.treatment?.next_visit_at ? `Next ${new Date(c.treatment.next_visit_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}` : 'Nothing scheduled'), c.treatment?.alerts?.[0]?.level],
              ['Liability', c.liability?.carrier || c.insurance_company || '—', c.liability?.claim_number || c.insurance_claim_number || null],
              ['Documents', docCount == null ? '…' : `${docCount} on file`, null],
              ['Referrals', refs == null ? '…' : `${refs.length} sent`, refs?.[0] ? REF_STATUS[refs[0].status] || refs[0].status : null],
            ].map(([k, v, sub, level]) => (
              <div key={k} className="bg-tertiary rounded-[12px] px-3.5 py-3 min-w-0">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-c">{k}</div>
                <div className="text-[14px] font-bold text-primary-c mt-0.5 truncate" title={String(v)}>{v}</div>
                {sub && <div className={`text-[10.5px] mt-0.5 truncate ${level === 'critical' ? 'text-danger' : level === 'warning' ? 'text-warning' : 'text-muted-c'}`} title={String(sub)}>{sub}</div>}
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-3 md:px-4 border-t border-theme overflow-x-auto">
          {TABS.map(([key, label, Icon]) => (
            <button key={key} onClick={() => setTab(key)} className={`inline-flex items-center gap-1.5 px-3.5 py-3 text-[12.5px] font-semibold border-b-2 whitespace-nowrap transition-all ${tab === key ? 'text-accent-light border-accent' : 'text-muted-c border-transparent hover:text-primary-c'}`}>
              <Icon size={13} /> {label}
              {key === 'documents' && docCount > 0 && <span className="ml-0.5 px-1.5 rounded-md bg-tertiary text-[10px] text-secondary-c">{docCount}</span>}
              {key === 'referrals' && refs?.length > 0 && <span className="ml-0.5 px-1.5 rounded-md bg-tertiary text-[10px] text-secondary-c">{refs.length}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* ── Documents ── */}
      {tab === 'documents' && (
        <CaseDocumentsTab orgId={currentOrg.id} caseRow={c} onChanged={() => api.listCaseDocuments(currentOrg.id, c.id).then((d) => setDocCount((d || []).length)).catch(() => {})} />
      )}

      {/* ── Referrals ── */}
      {tab === 'referrals' && (
        <div className="bg-elevated border border-theme rounded-[14px] overflow-hidden">
          {refs === null ? (
            <div className="px-4 py-8 text-[12.5px] text-muted-c flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Loading referrals…</div>
          ) : refs.length === 0 ? (
            <div className="px-4 py-10 text-center text-[12.5px] text-muted-c">No referrals on this case yet.{isFirm ? ' Use Refer a case to send it to a provider.' : ''}</div>
          ) : refs.map((r, i) => (
            <div key={r.id} className={`flex items-center gap-3 px-4 py-3.5 flex-wrap ${i ? 'border-t border-theme' : ''}`}>
              <div className="w-9 h-9 rounded-[10px] bg-accent-glow text-accent-light flex items-center justify-center shrink-0"><Send size={14} /></div>
              <div className="flex-1 min-w-[200px]">
                <div className="text-[13px] font-semibold text-primary-c">{r.service || 'Referral'}</div>
                <div className="text-[11.5px] text-muted-c">{isFirm ? `To ${r.provider?.name || 'provider'}` : `From ${r.firm?.name || 'law firm'}`} · {new Date(r.created_at).toLocaleDateString([], { dateStyle: 'medium' })}{r.scheduled_time ? ` · first visit ${new Date(r.scheduled_time).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}` : ''}</div>
              </div>
              {r.billing?.chip && <span className="text-[11px] font-semibold text-secondary-c bg-tertiary rounded-md px-2 py-0.5">{r.billing.chip}</span>}
              <CollectabilityBadge referral={r} />
              <span className="text-[11px] font-semibold text-secondary-c">{REF_STATUS[r.status] || r.status}</span>
              <Link to="/app/referrals" className="text-[11.5px] font-semibold text-accent-light">Open</Link>
            </div>
          ))}
        </div>
      )}

      {/* ── Timeline ── */}
      {tab === 'timeline' && (
        <div className="bg-elevated border border-theme rounded-[14px] p-5">
          <div className="flex flex-col gap-3">
            {(c.events || []).length === 0 && <p className="text-[12px] text-muted-c">No events yet.</p>}
            {(c.events || []).map((e) => (
              <div key={e.id} className="flex items-start gap-3 text-[12.5px]">
                <div className="w-2 h-2 rounded-full bg-accent mt-1.5 shrink-0" />
                <div className="flex-1">
                  <div className="text-primary-c font-medium">
                    {e.kind === 'status_changed' ? `Status: ${STATUS_LABEL[e.detail?.from] || e.detail?.from} → ${STATUS_LABEL[e.detail?.to] || e.detail?.to}`
                      : e.kind === 'coverage_added' ? `Coverage added: ${(e.detail?.coverages || [e.detail?.kind]).filter(Boolean).map((k) => KIND_LABEL[k] || k).join(', ')}`
                      : e.kind === 'coverage_status' ? `${KIND_LABEL[e.detail?.kind] || e.detail?.kind}: ${e.detail?.from} → ${e.detail?.to}`
                      : e.kind === 'emc_recorded' ? `EMC ${e.detail?.status}${e.detail?.provider_name ? ` by ${e.detail.provider_name}` : ''}`
                      : e.kind === 'charge_recorded' ? `${e.detail?.org_name || 'Clinic'} billed ${money(e.detail?.amount)} to ${(e.detail?.kind || '').toUpperCase()}${e.detail?.title ? ` — ${e.detail.title}` : ''}`
                      : e.kind === 'gap_alerted' ? `Treatment-gap alert sent to the firm (${e.detail?.days} days)`
                      : e.kind === 'created' ? 'Case opened'
                      : e.kind}
                  </div>
                  <div className="text-[10.5px] text-muted-c">{new Date(e.created_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-3 border-t border-theme text-[11px] text-muted-c flex items-center gap-1.5">
            <ShieldCheck size={12} /> Every change here is timestamped for the case record.
          </div>
        </div>
      )}

      {/* ── Overview: facts + funding ── */}
      {tab === 'overview' && (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {[
          ['Case type', <select value={c.case_type} onChange={(e) => run(() => api.updateCase(currentOrg.id, c.id, { case_type: e.target.value }))} className={`${inputClass} w-full`}>{Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>],
          ['Date of injury', <input type="date" defaultValue={c.accident_date || ''} onBlur={(e) => e.target.value !== (c.accident_date || '') && run(() => api.updateCase(currentOrg.id, c.id, { accident_date: e.target.value || null }))} className={`${inputClass} w-full`} />],
          ['State', <input maxLength={2} defaultValue={c.accident_state || ''} placeholder="FL" onBlur={(e) => e.target.value.toUpperCase() !== (c.accident_state || '') && run(() => api.updateCase(currentOrg.id, c.id, { accident_state: e.target.value.toUpperCase() || null }))} className={`${inputClass} w-full uppercase`} />],
          ['Liability carrier', <div className="text-[13px] text-primary-c font-medium py-2">{c.liability?.carrier || c.insurance_company || '—'}{(c.liability?.claim_number || c.insurance_claim_number) ? <span className="text-muted-c font-normal"> · {c.liability?.claim_number || c.insurance_claim_number}</span> : ''}</div>],
        ].map(([label, control]) => (
          <div key={label} className="bg-elevated border border-theme rounded-[12px] p-3">
            <div className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-c mb-1.5">{label}</div>
            {control}
          </div>
        ))}
      </div>
      )}

      <div className={`flex flex-col gap-4 ${['overview', 'treatment'].includes(tab) ? '' : 'hidden'}`}>
        <div className="flex flex-col gap-4">
          {/* Treatment — attendance and charges, straight from the clinics' calendars */}
          {tab === 'treatment' && c.treatment && (() => {
            const t = c.treatment;
            const fmtDT = (iso) => new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
            const ST = { completed: ['bg-success-soft text-success', 'Completed'], no_show: ['bg-danger-soft text-danger', 'No-show'], cancelled: ['bg-tertiary text-muted-c', 'Cancelled'], confirmed: ['bg-accent-glow text-accent-light', 'Confirmed'], pending: ['bg-warning-soft text-warning', 'Pending'] };
            return (
              <section className="bg-elevated border border-theme rounded-[14px] p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Clock size={15} className="text-accent-light" />
                  <h2 className="text-[15px] font-bold text-primary-c">Treatment</h2>
                  <span className="text-[11px] text-muted-c">— from the clinics' calendars, nothing typed</span>
                </div>
                {t.alerts.length > 0 && (
                  <div className="flex flex-col gap-1.5 mb-3">{t.alerts.map((a) => <AlertRow key={a.code} a={a} />)}</div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                  {[
                    ['Visits completed', t.visits_completed],
                    ['No-shows', t.no_shows],
                    ['Last visit', t.last_visit_at ? `${fmtDT(t.last_visit_at)}${t.days_since_last != null ? ` · ${t.days_since_last}d ago` : ''}` : '—'],
                    ['Next visit', t.next_visit_at ? `${fmtDT(t.next_visit_at)}${t.next_visit_org ? ` · ${t.next_visit_org}` : ''}` : 'Nothing scheduled'],
                  ].map(([k, v]) => (
                    <div key={k} className="bg-tertiary rounded-[12px] p-3">
                      <div className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-c">{k}</div>
                      <div className="text-[14px] font-bold text-primary-c mt-0.5 truncate" title={String(v)}>{v}</div>
                    </div>
                  ))}
                </div>
                {t.billed_total > 0 && (
                  <div className="text-[12px] text-secondary-c mb-2">Charges recorded by clinics: <strong className="text-primary-c">{money(t.billed_total)}</strong></div>
                )}
                {t.recent.length > 0 ? (
                  <div className="divide-y divide-theme">
                    {t.recent.map((v) => {
                      const [cls, label] = ST[v.status] || ['bg-tertiary text-muted-c', v.status];
                      return (
                        <div key={v.id} className="flex items-center gap-3 py-2 text-[12.5px]">
                          <span className="w-[76px] text-muted-c shrink-0">{fmtDT(v.starts_at)}</span>
                          <span className="flex-1 min-w-0 truncate text-primary-c">{v.title}{v.org_name ? <span className="text-muted-c"> · {v.org_name}</span> : ''}</span>
                          {v.billed_amount != null && <span className="text-[11.5px] text-secondary-c whitespace-nowrap">{money(v.billed_amount)} → {(v.billed_to || '').toUpperCase()}</span>}
                          <span className={`px-2 py-0.5 rounded-md text-[10.5px] font-semibold ${cls}`}>{label}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-[12.5px] text-muted-c">No visits on any clinic's calendar yet.</p>
                )}
              </section>
            );
          })()}

          {tab === 'overview' && (
          <section className="bg-elevated border border-theme rounded-[14px] p-5">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-c">Coverage · bill this visit to</div>
                <div className="text-[20px] font-bold text-primary-c">{f.current?.chip || '—'}</div>
                {f.rules?.has_pip && c.case_type === 'auto_mva' && <div className="text-[11px] text-muted-c">{f.rules.state} no-fault rules apply</div>}
                {c.case_type !== 'auto_mva' && <div className="text-[11px] text-muted-c">{TYPE_LABEL[c.case_type] || 'This'} case — no PIP; payers bill in the order below</div>}
              </div>
              <button onClick={() => setAddOpen((v) => !v)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[10px] text-[12px] font-semibold border border-theme bg-tertiary text-secondary-c hover:bg-hover-surface">
                <Plus size={13} /> Add coverage
              </button>
            </div>

            {f.alerts?.length > 0 && (
              <div className="flex flex-col gap-1.5 mb-4">
                {f.alerts.map((a) => <AlertRow key={a.code} a={a} />)}
              </div>
            )}

            {addOpen && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 bg-tertiary rounded-[12px] p-3 mb-4">
                <select value={add.kind} onChange={(e) => setAdd({ ...add, kind: e.target.value })} className={inputClass}>
                  {Object.entries(KIND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <input placeholder="Carrier" value={add.carrier} onChange={(e) => setAdd({ ...add, carrier: e.target.value })} className={inputClass} />
                <input placeholder="Claim / policy #" value={add.claim_number} onChange={(e) => setAdd({ ...add, claim_number: e.target.value })} className={inputClass} />
                <input placeholder="Limit $" type="number" value={add.limit_amount} onChange={(e) => setAdd({ ...add, limit_amount: e.target.value })} className={inputClass} />
                <button
                  disabled={busy}
                  onClick={() => run(async () => { const r = await api.addCaseCoverage(currentOrg.id, c.id, { ...add, limit_amount: add.limit_amount === '' ? null : Number(add.limit_amount), status: add.kind === 'lop' ? 'signed' : 'active', details: add.kind === 'pip' ? { emc_status: 'none' } : {} }); setAddOpen(false); setAdd({ kind: 'health', carrier: '', claim_number: '', limit_amount: '' }); return r; })}
                  className="px-3 py-2 rounded-[10px] text-[12px] font-semibold text-white gradient-accent disabled:opacity-50"
                >
                  Add
                </button>
              </div>
            )}

            <div className="flex flex-col divide-y divide-theme">
              {(f.path || []).length === 0 && <p className="text-[12.5px] text-muted-c py-2">No coverage recorded yet.</p>}
              {(f.path || []).map((cv) => (
                <div key={cv.id} className="py-3 first:pt-0 last:pb-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-0.5 rounded-md text-[10.5px] font-bold ${KIND_CLS[cv.kind]}`}>{cv.label}</span>
                    <span className="text-[13px] font-semibold text-primary-c">{cv.carrier || ''}</span>
                    {cv.claim_number && <span className="text-[11.5px] font-mono text-muted-c">{cv.claim_number}</span>}
                    <span className="flex-1" />
                    <select
                      value={cv.status}
                      onChange={(e) => run(() => api.updateCaseCoverage(currentOrg.id, c.id, cv.id, { status: e.target.value }))}
                      className={`${inputClass} py-1 text-[12px]`}
                    >
                      {COV_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button onClick={() => run(() => api.removeCaseCoverage(currentOrg.id, c.id, cv.id))} className="text-muted-c hover:text-danger" title="Remove">
                      <Trash2 size={13} />
                    </button>
                  </div>
                  {['lop', 'health', 'self_pay'].includes(cv.kind) ? (
                    // Open-ended payers: no cap to track — the number that matters is the running balance.
                    <div className="flex items-end gap-5 mt-2 flex-wrap">
                      <label className="text-[10.5px] text-muted-c">{cv.kind === 'lop' ? 'Lien balance — billed so far' : 'Billed so far'}
                        <input type="number" defaultValue={cv.used_amount ?? 0} onBlur={(e) => Number(e.target.value) !== Number(cv.used_amount || 0) && run(() => api.updateCaseCoverage(currentOrg.id, c.id, cv.id, { used_amount: Number(e.target.value) || 0 }))} className={`${inputClass} w-[140px] block mt-0.5 py-1`} />
                      </label>
                      {cv.policy_number && <div className="text-[11.5px] text-secondary-c pb-2">{cv.kind === 'health' ? 'Member ID' : 'Policy'} <span className="font-mono">{cv.policy_number}</span></div>}
                      <div className="text-[11px] text-muted-c pb-2">
                        {cv.kind === 'lop' ? 'No dollar limit — the clinic treats on the letter and is paid from the settlement; visits the clinic records add to this balance.'
                          : cv.kind === 'health' ? 'No limit tracked — billed per the plan; visits recorded against it add up here.'
                          : 'Patient pays directly.'}
                      </div>
                    </div>
                  ) : (
                  <div className="grid grid-cols-3 gap-3 mt-2 max-w-[440px]">
                    <label className="text-[10.5px] text-muted-c">Limit
                      <input type="number" defaultValue={cv.limit_amount ?? ''} onBlur={(e) => String(e.target.value) !== String(cv.limit_amount ?? '') && run(() => api.updateCaseCoverage(currentOrg.id, c.id, cv.id, { limit_amount: e.target.value === '' ? null : Number(e.target.value) }))} className={`${inputClass} w-full mt-0.5 py-1`} />
                    </label>
                    <label className="text-[10.5px] text-muted-c">Used
                      <input type="number" defaultValue={cv.used_amount ?? 0} onBlur={(e) => Number(e.target.value) !== Number(cv.used_amount || 0) && run(() => api.updateCaseCoverage(currentOrg.id, c.id, cv.id, { used_amount: Number(e.target.value) || 0 }))} className={`${inputClass} w-full mt-0.5 py-1`} />
                    </label>
                    <div className="text-[10.5px] text-muted-c">Remaining
                      <div className="text-[14px] font-bold text-primary-c mt-1.5">{cv.effective_remaining != null ? money(cv.effective_remaining) : '—'}</div>
                    </div>
                  </div>
                  )}
                  {cv.note && <div className="text-[11px] text-warning mt-1.5">{cv.note}</div>}
                </div>
              ))}
            </div>
          </section>
          )}

          {/* EMC — only meaningful for PIP in a no-fault state */}
          {tab === 'overview' && pip && f.rules?.has_pip && (
            <section className="bg-elevated border border-theme rounded-[14px] p-5">
              <div className="flex items-center gap-2 mb-1">
                <Stethoscope size={15} className="text-accent-light" />
                <h2 className="text-[15px] font-bold text-primary-c">Emergency Medical Condition</h2>
              </div>
              <p className="text-[12.5px] text-muted-c mb-3">
                Without an EMC determination, PIP pays up to $2,500. Only an MD, DO, PA, APRN or dentist can make it — chiropractors cannot.
                Current: <strong className="text-primary-c">{pip.details?.emc_status || 'none'}</strong>
                {pip.details?.emc_provider ? ` by ${pip.details.emc_provider}` : ''}{pip.details?.emc_date ? ` on ${fmtD(pip.details.emc_date)}` : ''}.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <select value={emc.status} onChange={(e) => setEmc({ ...emc, status: e.target.value })} className={inputClass}>
                  {['determined', 'pending', 'denied', 'none'].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <input placeholder="Determining provider" value={emc.provider_name} onChange={(e) => setEmc({ ...emc, provider_name: e.target.value })} className={inputClass} />
                <input type="date" value={emc.determined_at} onChange={(e) => setEmc({ ...emc, determined_at: e.target.value })} className={inputClass} />
                <button disabled={busy} onClick={() => run(() => api.recordCaseEmc(currentOrg.id, c.id, emc))} className="px-3 py-2 rounded-[10px] text-[12px] font-semibold text-white gradient-accent disabled:opacity-50">
                  Record EMC
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3 max-w-[440px]">
                <label className="text-[10.5px] text-muted-c">First treatment date (14-day rule)
                  <input type="date" defaultValue={pip.details?.first_treatment_date || ''} onBlur={(e) => e.target.value !== (pip.details?.first_treatment_date || '') && run(() => api.updateCaseCoverage(currentOrg.id, c.id, pip.id, { details: { first_treatment_date: e.target.value || null } }))} className={`${inputClass} w-full mt-0.5`} />
                </label>
              </div>
            </section>
          )}

          {/* PIP billing details — what a clinic needs to bill this claim.
              Columns (policy, adjuster) and details (insured, where bills go)
              save on blur; the referral packet reads all of it. */}
          {tab === 'overview' && pip && (
            <section className="bg-elevated border border-theme rounded-[14px] p-5">
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck size={15} className="text-accent-light" />
                <h2 className="text-[15px] font-bold text-primary-c">PIP billing details</h2>
              </div>
              <p className="text-[12.5px] text-muted-c mb-3">
                Identifies the claim and says where the bill goes. Travels with every referral so the clinic's billing office can submit without calling the adjuster.
              </p>
              {(() => {
                const d = pip.details || {};
                const col = (field, label, type = 'text') => (
                  <label key={field} className="text-[10.5px] text-muted-c">{label}
                    <input type={type} defaultValue={pip[field] ?? ''} onBlur={(e) => (e.target.value || '') !== (pip[field] || '') && run(() => api.updateCaseCoverage(currentOrg.id, c.id, pip.id, { [field]: e.target.value || null }))} className={`${inputClass} w-full mt-0.5`} />
                  </label>
                );
                const det = (key, label, type = 'text') => (
                  <label key={key} className="text-[10.5px] text-muted-c">{label}
                    <input type={type} defaultValue={d[key] ?? ''} onBlur={(e) => (e.target.value || '') !== (d[key] || '') && run(() => api.updateCaseCoverage(currentOrg.id, c.id, pip.id, { details: { [key]: e.target.value || null } }))} className={`${inputClass} w-full mt-0.5`} />
                  </label>
                );
                return (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                    {col('policy_number', 'Policy #')}
                    {det('named_insured', 'Named insured')}
                    <label className="text-[10.5px] text-muted-c">Client's relationship to insured
                      <select value={d.insured_relationship || ''} onChange={(e) => run(() => api.updateCaseCoverage(currentOrg.id, c.id, pip.id, { details: { insured_relationship: e.target.value || null } }))} className={`${inputClass} w-full mt-0.5`}>
                        <option value="">—</option>
                        {['self', 'spouse', 'parent', 'child', 'other'].map((v) => <option key={v} value={v}>{v === 'self' ? 'Self (policyholder)' : v[0].toUpperCase() + v.slice(1)}</option>)}
                      </select>
                    </label>
                    <label className="text-[10.5px] text-muted-c md:col-span-2">Claims mailing address (where PIP bills go)
                      <input defaultValue={d.billing_address ?? ''} onBlur={(e) => (e.target.value || '') !== (d.billing_address || '') && run(() => api.updateCaseCoverage(currentOrg.id, c.id, pip.id, { details: { billing_address: e.target.value || null } }))} className={`${inputClass} w-full mt-0.5`} placeholder="Carrier PIP Claims, P.O. Box …, City, ST ZIP" />
                    </label>
                    {det('payer_id', 'Electronic payer ID')}
                    {det('billing_fax', 'Claims fax')}
                    {col('adjuster_name', 'PIP adjuster')}
                    {col('adjuster_phone', 'Adjuster phone', 'tel')}
                    {det('adjuster_email', 'Adjuster email', 'email')}
                    <label className="text-[10.5px] text-muted-c">Client was
                      <select value={c.liability?.patient_role || ''} onChange={(e) => run(() => api.updateCase(currentOrg.id, c.id, { liability: { ...(c.liability || {}), patient_role: e.target.value || null } }))} className={`${inputClass} w-full mt-0.5`}>
                        <option value="">—</option>
                        {['driver', 'passenger', 'pedestrian', 'cyclist', 'other'].map((v) => <option key={v} value={v}>{v[0].toUpperCase() + v.slice(1)}</option>)}
                      </select>
                    </label>
                  </div>
                );
              })()}
            </section>
          )}
        </div>

      </div>
    </div>
  );
}
